import { ConnectorState, Selection } from "./types";

/**
 * What a tracked competitor's pricing looks like *to the client*.
 *
 * ⚠️ Deliberately not the connector's own status. `WAITING_FOR_REVIEW`, `IN_CREATION` and
 * `BROKEN` are our operational vocabulary — they name what somebody at Revbell has to
 * do, and a customer reading "BROKEN" on their own compset learns nothing they can
 * act on. The five states below answer the only question the page raises: is this
 * competitor being priced, and if not, is anyone dealing with it.
 */
export type ConnectorHealth =
  | "active"
  | "setup"
  | "failing"
  | "paused"
  | "none";

/**
 * The best state among a competitor's connectors, not the worst.
 *
 * ⚠️ The order matters and is not arbitrary. A competitor can carry several connectors, and
 * the client's question is "are my prices flowing", not "is every connector healthy" — so
 * one working connector beside one broken one reads as `active`. Reporting the worst would
 * put a red label on a row whose prices are arriving perfectly well, and a warning
 * that fires when nothing is wrong is one people learn to ignore.
 *
 * ⚠️ `none` covers two situations the API keeps apart: no depth connector ever wrote about
 * this competitor, and every connector that did has since been deleted. They differ in
 * whose fault they are, but not in what the client sees — a property-level price and
 * no room detail — so the page says the same thing for both. `connectors` is also empty
 * when the sweep was unreachable, which is a third road to the same honest answer:
 * we cannot tell you right now.
 */
export const connectorHealth = (
  connectors: ConnectorState[]
): ConnectorHealth => {
  const live = connectors.filter((connector) => connector.exists);
  if (
    live.some((connector) => connector.status === "OK" && connector.isActive)
  ) {
    return "active";
  }
  if (
    live.some(
      (connector) =>
        connector.status === "IN_CREATION" ||
        connector.status === "WAITING_FOR_REVIEW"
    )
  ) {
    return "setup";
  }
  if (live.some((connector) => connector.status === "BROKEN")) {
    return "failing";
  }
  if (live.length > 0) {
    return "paused";
  }
  return "none";
};

/**
 * Palette token per state, matching `Sweep/Connectors/STATUS_TOKEN`'s vocabulary.
 *
 * `none` is muted rather than coloured: it is the majority case for a compset built
 * from Google alone, and colouring the common case leaves nothing for the states that
 * need attention.
 */
export const HEALTH_TOKEN: Record<ConnectorHealth, string> = {
  active: "success.main",
  setup: "info.main",
  failing: "error.main",
  paused: "text.disabled",
  none: "text.disabled",
};

export const HEALTH_LABEL: Record<ConnectorHealth, string> = {
  active: "COMPETITOR_SELECTION_PRICING_ACTIVE",
  setup: "COMPETITOR_SELECTION_PRICING_SETUP",
  failing: "COMPETITOR_SELECTION_PRICING_FAILING",
  paused: "COMPETITOR_SELECTION_PRICING_PAUSED",
  none: "COMPETITOR_SELECTION_PRICING_NONE",
};

export const HEALTH_TOOLTIP: Record<ConnectorHealth, string> = {
  active: "COMPETITOR_SELECTION_PRICING_ACTIVE_TOOLTIP",
  setup: "COMPETITOR_SELECTION_PRICING_SETUP_TOOLTIP",
  failing: "COMPETITOR_SELECTION_PRICING_FAILING_TOOLTIP",
  paused: "COMPETITOR_SELECTION_PRICING_PAUSED_TOOLTIP",
  none: "COMPETITOR_SELECTION_PRICING_NONE_TOOLTIP",
};

/** Convenience for the list row. */
export const healthOf = (item: Selection): ConnectorHealth =>
  connectorHealth(item.connectors);
